import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Wallet, Send, CheckCircle2, Clock } from "lucide-react";
import { getCpCommissions, submitCpRemittance } from "../../services/agentReferral";

const STATUS_META = {
  PENDING_PAYMENT: { label: "Payment Due", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  PENDING_VALIDATION: { label: "Awaiting BuildVizer Validation", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  REDEEMABLE: { label: "Validated", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  REDEEMED: { label: "Completed", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const RemitModal = ({ commission, onClose, onDone }) => {
  const [reference, setReference] = useState("");
  const [amount, setAmount] = useState(commission?.rewardAmount ?? "");
  const [proofUrl, setProofUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!reference.trim() || !amount) {
      toast.error("Enter the transfer reference and amount");
      return;
    }
    setSaving(true);
    try {
      await submitCpRemittance(commission.id, {
        reference: reference.trim(),
        amount: Number(amount),
        proofUrl: proofUrl.trim() || null,
      });
      toast.success("Remittance submitted — BuildVizer will validate it shortly");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit remittance");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-gray-800">Remit BuildVizer cut</h3>
        <p className="mt-1 text-sm text-gray-500">
          Transfer the agreed cut to BuildVizer, then record the details below. BuildVizer validates the
          payment to unlock the referring buyer's reward.
        </p>
        <div className="mt-4 space-y-3">
          <label className="block text-sm">
            <span className="text-gray-600">Amount</span>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-600">Transfer reference / UTR</span>
            <input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. UTR / txn id"
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-600">Proof URL (optional)</span>
            <input
              value={proofUrl}
              onChange={(e) => setProofUrl(e.target.value)}
              placeholder="Link to a receipt/screenshot"
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-lg bg-flamingo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-flamingo-700 disabled:opacity-50"
          >
            <Send size={14} /> Submit remittance
          </button>
        </div>
      </div>
    </div>
  );
};

const ReferralCommissions = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [remit, setRemit] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCpCommissions();
      setRows(Array.isArray(res) ? res : res?.items || []);
    } catch (e) {
      toast.error("Failed to load referral commissions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="max-w-4xl mx-auto px-2 sm:px-4 md:px-8 py-4 md:py-8 animate-fade-in">
      <div className="mb-6 flex items-center gap-3">
        <Wallet className="h-7 w-7 text-flamingo-500" />
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Referral Commissions</h2>
          <p className="text-sm text-gray-500">
            Cuts you owe BuildVizer for referrals passed on to you. Remit the cut and BuildVizer validates it.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 overflow-x-auto">
        {loading ? (
          <p className="py-8 text-center text-sm text-gray-500">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center">
            <Wallet className="mx-auto h-7 w-7 text-gray-300" />
            <p className="mt-2 text-base font-semibold text-gray-800">No referral commissions yet</p>
            <p className="mt-1 text-sm text-gray-500">Commissions appear here when a buyer you sold to refers someone.</p>
          </div>
        ) : (
          <table className="min-w-full text-xs sm:text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-gray-600">
                <th className="py-2 px-2 whitespace-nowrap">Referred</th>
                <th className="py-2 px-2 whitespace-nowrap">Project</th>
                <th className="py-2 px-2 whitespace-nowrap">Commission</th>
                <th className="py-2 px-2 whitespace-nowrap">Cut owed to BV</th>
                <th className="py-2 px-2 whitespace-nowrap">Status</th>
                <th className="py-2 px-2 whitespace-nowrap text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.PENDING_PAYMENT;
                return (
                  <tr key={r.id} className="border-b border-gray-50 hover:bg-flamingo-50/40 transition">
                    <td className="py-2 px-2 text-gray-700 whitespace-nowrap">{r.referredName || `User #${r.referredUserId}`}</td>
                    <td className="py-2 px-2 text-gray-700 whitespace-nowrap">{r.projectName || "—"}</td>
                    <td className="py-2 px-2 text-gray-700 whitespace-nowrap">{r.commissionAmount ?? "—"}</td>
                    <td className="py-2 px-2 font-semibold text-gray-900 whitespace-nowrap">{r.rewardAmount}</td>
                    <td className="py-2 px-2 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}>
                        {r.status === "REDEEMABLE" || r.status === "REDEEMED" ? <CheckCircle2 size={12} /> : <Clock size={12} />}
                        {meta.label}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-right whitespace-nowrap">
                      {r.status === "PENDING_PAYMENT" ? (
                        <button
                          onClick={() => setRemit(r)}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-flamingo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-flamingo-700"
                        >
                          <Send size={13} /> Remit cut
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">{r.cpRemittanceReference ? `Ref: ${r.cpRemittanceReference}` : "—"}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {remit ? (
        <RemitModal
          commission={remit}
          onClose={() => setRemit(null)}
          onDone={() => {
            setRemit(null);
            load();
          }}
        />
      ) : null}
    </div>
  );
};

export default ReferralCommissions;
